// Export d'une figure : image (SVG, PNG) et tableau (CSV). Portage de
// poc/src/export.js. L'image sert les diapositives et comptes rendus ; le tableau
// sert à vérifier un chiffre lu sur un graphique et tient lieu de vue sans couleur.

export interface Tableau {
  colonnes: string[];
  lignes: (string | number | null)[][];
}

const NS = "http://www.w3.org/2000/svg";

// Un SVG exporté n'emporte pas la feuille de style : on fige ces propriétés, avec
// les variables CSS résolues.
const PROPRIETES = [
  "fill",
  "fill-opacity",
  "stroke",
  "stroke-width",
  "stroke-opacity",
  "stroke-dasharray",
  "stroke-linejoin",
  "opacity",
  "font-size",
  "font-family",
  "font-weight",
  "text-anchor",
  "paint-order",
  "display",
  "visibility",
];

// XMLSerializer n'échappe pas les guillemets doubles d'une valeur d'attribut
// (`font-family`) : le fichier serait du XML invalide.
const assainir = (v: string) => v.replace(/"/g, "'");

function figerStyles(source: Element, copie: Element): void {
  const calcule = window.getComputedStyle(source);
  for (const p of PROPRIETES) {
    const v = calcule.getPropertyValue(p);
    if (v) copie.setAttribute(p, assainir(v));
  }
  Array.from(source.children).forEach((enfant, j) => {
    const double = copie.children[j];
    if (double) figerStyles(enfant, double);
  });
}

/** SVG autonome, styles figés, fond opaque (lisible collé sur une diapositive sombre). */
export function serialiserSvg(
  svg: SVGSVGElement,
  fond: string,
): { texte: string; largeur: number; hauteur: number } {
  const copie = svg.cloneNode(true) as SVGSVGElement;
  // Parcours parallèle des deux arbres : à faire avant toute suppression.
  figerStyles(svg, copie);
  copie.querySelectorAll("[data-export='non']").forEach((e) => e.remove());
  const vb = svg.viewBox?.baseVal;
  const boite =
    vb && vb.width
      ? { x: vb.x, y: vb.y, width: vb.width, height: vb.height }
      : { x: 0, y: 0, width: svg.clientWidth, height: svg.clientHeight };
  copie.setAttribute("xmlns", NS);
  copie.setAttribute("width", String(boite.width));
  copie.setAttribute("height", String(boite.height));
  copie.removeAttribute("class");
  const rect = document.createElementNS(NS, "rect");
  for (const [k, v] of Object.entries({
    x: boite.x,
    y: boite.y,
    width: boite.width,
    height: boite.height,
  }))
    rect.setAttribute(k, String(v));
  rect.setAttribute("fill", fond);
  copie.insertBefore(rect, copie.firstChild);
  return {
    texte: new XMLSerializer().serializeToString(copie),
    largeur: boite.width,
    hauteur: boite.height,
  };
}

function telecharger(blob: Blob, nom: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nom;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Révoquer tout de suite annule le téléchargement sur certains navigateurs.
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/** Le fond de la zone des figures, neutre quelle que soit la palette d'interface. */
const fondCourant = () =>
  window.getComputedStyle(document.documentElement).getPropertyValue("--figure-fond").trim() ||
  window.getComputedStyle(document.body).getPropertyValue("background-color") ||
  "#ffffff";

export const nomFichier = (figure: string, jour = new Date().toISOString().slice(0, 10)) =>
  `parsada-map-${figure}-${jour}`;

export function exporterSvg(svg: SVGSVGElement, nom: string): void {
  const s = serialiserSvg(svg, fondCourant());
  telecharger(new Blob([s.texte], { type: "image/svg+xml;charset=utf-8" }), `${nom}.svg`);
}

/** PNG à deux fois la taille d'écran : il finit imprimé ou projeté. */
export function exporterPng(
  svg: SVGSVGElement,
  nom: string,
  surEchec: (message: string) => void,
): void {
  const fond = fondCourant();
  const s = serialiserSvg(svg, fond);
  const echelle = 2;
  const image = new Image();
  image.onload = () => {
    try {
      const toile = document.createElement("canvas");
      toile.width = Math.round(s.largeur * echelle);
      toile.height = Math.round(s.hauteur * echelle);
      const ctx = toile.getContext("2d");
      if (!ctx) throw new Error("canvas indisponible");
      ctx.fillStyle = fond;
      ctx.fillRect(0, 0, toile.width, toile.height);
      ctx.drawImage(image, 0, 0, toile.width, toile.height);
      toile.toBlob((blob) => {
        if (blob) telecharger(blob, `${nom}.png`);
        else surEchec("Le navigateur n'a pas produit d'image.");
      }, "image/png");
    } catch (e) {
      surEchec(
        `Export PNG refusé par le navigateur (${(e as Error).name}). L'export SVG fonctionne.`,
      );
    }
  };
  image.onerror = () => surEchec("Conversion en image impossible. L'export SVG fonctionne.");
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(s.texte)}`;
}

/**
 * CSV `;` avec BOM, comme le reste du projet : s'ouvre proprement dans un tableur
 * en locale française.
 */
export function texteCsv(tableau: Tableau): string {
  const echapper = (v: string | number | null | undefined) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lignes = [tableau.colonnes.map(echapper).join(";")];
  for (const l of tableau.lignes) lignes.push(l.map(echapper).join(";"));
  return `\uFEFF${lignes.join("\r\n")}\r\n`;
}

export function exporterCsv(tableau: Tableau, nom: string): void {
  telecharger(new Blob([texteCsv(tableau)], { type: "text/csv;charset=utf-8" }), `${nom}.csv`);
}

/** Tableau d'une matrice : une ligne par ligne, une colonne par colonne. */
export function tableauDepuisMatrice(
  lignes: string[],
  colonnes: string[],
  valeur: (i: number, j: number) => number | string | null,
  formater: (v: number) => string = String,
): Tableau {
  return {
    colonnes: ["", ...colonnes],
    lignes: lignes.map((l, i) => [
      l,
      ...colonnes.map((_, j) => {
        const v = valeur(i, j);
        return typeof v === "number" ? formater(v) : v;
      }),
    ]),
  };
}
